export const SHOPIFY_BIND_PATH = '/shopify-bind-account'
export const SHOPIFY_LANDING_PATH = '/shopify-landing'
export const SHOPIFY_ADD_STORE_PATH = '/shopify-add-store'
export const IMPERSONATE_PATH = '/impersonate'

export type ConnectTicketPath = typeof SHOPIFY_BIND_PATH | typeof SHOPIFY_LANDING_PATH | typeof SHOPIFY_ADD_STORE_PATH | typeof IMPERSONATE_PATH

const TICKET_PATHS: readonly string[] = [SHOPIFY_BIND_PATH, SHOPIFY_LANDING_PATH, SHOPIFY_ADD_STORE_PATH, IMPERSONATE_PATH]
const TICKET_PARAM = 'ticket'
const TICKET_KEY_PREFIX = 'buyscout.connectTicket:'
const OUTCOME_KEY_PREFIX = 'buyscout.connectOutcome:'

export interface ConnectTicketEnvironment {
  location: Pick<Location, 'pathname' | 'search' | 'hash'>
  history: Pick<History, 'state' | 'replaceState'>
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null
}

/** How a finished connect flow ended, kept so that coming back to the page shows it again. */
export interface ConnectOutcome {
  status: 'used' | 'expired'
  shop: string
  returnUrl: string
}

function browserEnvironment(): ConnectTicketEnvironment {
  let storage: Storage | null = null
  try {
    storage = window.sessionStorage
  } catch {}
  return { location: window.location, history: window.history, storage }
}

function ticketFromSearch(search: string): string | null {
  return new URLSearchParams(search).get(TICKET_PARAM)?.trim() || null
}

function setItem(env: ConnectTicketEnvironment, key: string, value: string): boolean {
  try {
    env.storage?.setItem(key, value)
    return Boolean(env.storage)
  } catch {
    return false
  }
}

function getItem(env: ConnectTicketEnvironment, key: string): string | null {
  try {
    return env.storage?.getItem(key) ?? null
  } catch {
    return null
  }
}

function removeItem(env: ConnectTicketEnvironment, key: string): void {
  try {
    env.storage?.removeItem(key)
  } catch {}
}

export function withoutTicket(search: string): string {
  const params = new URLSearchParams(search)
  params.delete(TICKET_PARAM)
  const rest = params.toString()
  return rest ? `?${rest}` : ''
}

/**
 * Moves a connect ticket out of the address bar into this tab's session storage
 * before anything can record the URL: a ticket stays redeemable until it is used
 * or expires, and the address bar feeds telemetry, history and copied links.
 * When storage is unavailable the ticket stays in the URL so the page still works.
 */
export function stashConnectTicket(env: ConnectTicketEnvironment = browserEnvironment()): void {
  const { location, history } = env
  if (!TICKET_PATHS.includes(location.pathname) || !new URLSearchParams(location.search).has(TICKET_PARAM)) {
    return
  }
  const ticket = ticketFromSearch(location.search)
  if (ticket) {
    if (!setItem(env, TICKET_KEY_PREFIX + location.pathname, ticket)) {
      return
    }
    removeItem(env, OUTCOME_KEY_PREFIX + location.pathname)
  }
  history.replaceState(history.state, '', `${location.pathname}${withoutTicket(location.search)}${location.hash}`)
}

/** A ticket still in the URL is newer than a stored one, so it wins. */
export function readConnectTicket(path: ConnectTicketPath, env: ConnectTicketEnvironment = browserEnvironment()): string | null {
  const fromUrl = env.location.pathname === path ? ticketFromSearch(env.location.search) : null
  return fromUrl ?? getItem(env, TICKET_KEY_PREFIX + path)
}

export function clearConnectTicket(path: ConnectTicketPath, env: ConnectTicketEnvironment = browserEnvironment()): void {
  removeItem(env, TICKET_KEY_PREFIX + path)
}

export function rememberConnectOutcome(
  path: ConnectTicketPath,
  outcome: ConnectOutcome,
  env: ConnectTicketEnvironment = browserEnvironment(),
): void {
  setItem(env, OUTCOME_KEY_PREFIX + path, JSON.stringify(outcome))
}

export function readConnectOutcome(path: ConnectTicketPath, env: ConnectTicketEnvironment = browserEnvironment()): ConnectOutcome | null {
  const raw = getItem(env, OUTCOME_KEY_PREFIX + path)
  if (!raw) {
    return null
  }
  try {
    const { status, shop, returnUrl } = JSON.parse(raw) as Partial<ConnectOutcome>
    if ((status === 'used' || status === 'expired') && typeof shop === 'string' && typeof returnUrl === 'string' && /^https?:\/\//.test(returnUrl)) {
      return { status, shop, returnUrl }
    }
  } catch {}
  return null
}

export function connectTicketHeaders(ticket: string): { headers: Record<string, string> } {
  return { headers: { 'X-Connect-Ticket': ticket } }
}
